import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Flame, Heart, Diamond, User, Settings, LogOut, Sun, Moon, Bell, BarChart3, Calendar, ShieldCheck, MessageCircle } from 'lucide-react';
import api from '../api/client';

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  const unreadCount = notifs.filter((n) => !n.read).length;

  useEffect(() => {
    const load = () => api.get('/notifications').then((r) => setNotifs(r.data || [])).catch(() => {});
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleBellClick = async () => {
    setShowNotifs((p) => !p);
    if (unreadCount > 0) {
      await api.post('/notifications/mark-read').catch(() => {});
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  const NAV_MAIN = [
    { path: '/discover', label: t('tabs.discover'), icon: Flame },
    { path: '/matches', label: t('tabs.matches'), icon: Heart },
    { path: '/chat-hub', label: 'Messages', icon: MessageCircle },
  ];

  const NAV_WEB_EXCLUSIVE = [
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/events', label: 'Events', icon: Calendar },
  ];

  const NAV_BOTTOM = [
    { path: '/store', label: t('tabs.store'), icon: Diamond },
    { path: '/profile', label: t('tabs.profile'), icon: User },
    { path: '/security', label: 'Securite', icon: ShieldCheck },
    { path: '/settings', label: t('settings.title'), icon: Settings },
  ];

  const handleLogout = () => { logout(); navigate('/'); };

  const NavItem = ({ path, label, icon: Icon }) => (
    <NavLink to={path}
      className={({ isActive }) => `flex items-center gap-4 px-4 py-3 rounded-xl transition-all group ${isActive ? 'bg-brand-violet/15' : 'hover:bg-[var(--bg-card-hover)]'}`}
      style={({ isActive }) => ({ color: isActive ? 'var(--text-primary)' : 'var(--text-muted)' })}
      data-testid={`sidebar-${path.slice(1)}`}
    >
      <Icon size={19} className="shrink-0" />
      <span className="text-sm font-semibold hidden lg:block">{label}</span>
    </NavLink>
  );

  return (
    <aside className="w-20 lg:w-64 h-full flex flex-col shrink-0 relative" style={{background:'var(--bg-secondary)', borderRight:'1px solid var(--border-color)'}}>
      <div className="flex items-center gap-3 px-6 py-5" style={{borderBottom:'1px solid var(--border-color)'}}>
        <img src="/logo.png" alt="AHOOW" className="h-9 w-9 rounded-xl shrink-0" />
        <span className="text-xl font-black hidden lg:block" style={{color:'var(--text-primary)'}}>AH<span className="text-brand-pink">OO</span>W</span>
      </div>

      <nav className="flex-1 py-3 px-3 overflow-y-auto space-y-0.5">
        {NAV_MAIN.map((item) => <NavItem key={item.path} {...item} />)}

        <div className="pt-3 pb-1 px-4 hidden lg:block">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{color:'var(--text-faint)'}}>Web Exclusif</span>
        </div>
        <div className="hidden lg:hidden border-t my-2" style={{borderColor:'var(--border-color)'}} />
        {NAV_WEB_EXCLUSIVE.map((item) => <NavItem key={item.path} {...item} />)}

        <div className="pt-3 pb-1 px-4 hidden lg:block">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{color:'var(--text-faint)'}}>Compte</span>
        </div>
        {NAV_BOTTOM.map((item) => <NavItem key={item.path} {...item} />)}
      </nav>

      <div className="px-3 pb-2 space-y-0.5">
        <div className="relative" ref={notifRef}>
          <button onClick={handleBellClick}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all hover:bg-[var(--bg-card-hover)]"
            style={{color:'var(--text-muted)'}} data-testid="sidebar-notifications"
          >
            <div className="relative shrink-0">
              <Bell size={19} />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-brand-pink text-white text-[10px] font-bold flex items-center justify-center">{unreadCount}</span>
              )}
            </div>
            <span className="text-sm font-semibold hidden lg:block">Notifications</span>
          </button>
          {showNotifs && (
            <div className="absolute bottom-full left-0 lg:left-full lg:bottom-auto lg:top-0 mb-2 lg:mb-0 lg:ml-2 w-72 glass-card p-2 max-h-80 overflow-y-auto z-50 shadow-xl" style={{border:'1px solid var(--border-color)'}}>
              {notifs.length === 0 ? (
                <p className="text-sm text-center py-4" style={{color:'var(--text-muted)'}}>Aucune notification</p>
              ) : notifs.slice(0, 10).map((n) => (
                <div key={n.id} className="p-3 rounded-lg mb-1 transition-colors hover:bg-[var(--bg-card-hover)]" style={{background: n.read ? 'transparent' : 'var(--bg-card)'}}>
                  <p className="text-xs font-semibold" style={{color:'var(--text-primary)'}}>{n.title}</p>
                  <p className="text-xs mt-0.5" style={{color:'var(--text-muted)'}}>{n.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={toggleTheme}
          className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all hover:bg-[var(--bg-card-hover)]"
          style={{color:'var(--text-muted)'}} data-testid="sidebar-theme-toggle"
        >
          {theme === 'dark' ? <Sun size={19} className="shrink-0" /> : <Moon size={19} className="shrink-0" />}
          <span className="text-sm font-semibold hidden lg:block">{theme === 'dark' ? 'Mode Jour' : 'Mode Nuit'}</span>
        </button>
      </div>

      <div className="p-3" style={{borderTop:'1px solid var(--border-color)'}}>
        {user && (
          <div className="flex items-center gap-3 px-4 py-2 mb-1">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-pink to-brand-violet flex items-center justify-center text-white font-bold text-sm shrink-0">
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="hidden lg:block min-w-0">
              <p className="text-sm font-semibold truncate" style={{color:'var(--text-primary)'}}>{user.name}</p>
              <p className="text-xs truncate" style={{color:'var(--text-faint)'}}>{user.email}</p>
            </div>
          </div>
        )}
        <button onClick={handleLogout}
          className="w-full flex items-center gap-4 px-4 py-2.5 rounded-xl hover:text-red-400 hover:bg-red-500/10 transition-all"
          style={{color:'var(--text-faint)'}} data-testid="sidebar-logout"
        >
          <LogOut size={19} className="shrink-0" />
          <span className="text-sm font-semibold hidden lg:block">{t('settings.logout')}</span>
        </button>
      </div>
    </aside>
  );
}
